import React from 'react'
import CoachServicesPage from './CoachServicesPage'
import { TranslationProvider } from '~/components/shared/TranslationProvider'
import BenefitsProvider, { useBenefits } from '../BenefitsProvider'
import translations from '../locales.json'
import YnsIcon from '../../icons/YnsIcon'
import { Card } from '~/components/_atoms'
import parse from 'html-react-parser'
import { useTranslation } from '~/hooks/useTranslation'
import ScheduleInner from './ScheduleInner'
import moment from 'moment'
import 'moment-timezone'
import { Button } from '~/lib/ui/button'
import { SERVICE_REQUEST_ICONS } from '../ServiceRequestIcons'
import CandidateReviewFeedbackDialog from '../ResumeReview/CandidateFeedback/CandidateReviewFeedbackDialog'

const ScheduledCard = ({ calendlyEvent }) => {
  const { onCancelClick } = useBenefits()

  return (
    <div className="flex items-center gap-4">
      <YnsIcon name="calendar" className="mr-2 h-20 w-20" />
      <div className="flex flex-grow flex-col gap-4">
        <div>
          <span className="text-md font-semibold">Schedule Date: </span>
          {moment(calendlyEvent.start_time).format('MM/DD/YY')}
        </div>
        <div>
          <span className="text-md font-semibold">Schedule Time: </span>
          {moment(calendlyEvent.start_time)
            .tz(moment.tz.guess())
            .format('hh:mm A z')}
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <a
          href={calendlyEvent.join_url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full"
        >
          <Button variant="coachcta" className="w-full">
            Join Meeting
          </Button>
        </a>
        <a
          href={calendlyEvent.cancel_url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full"
          onClick={onCancelClick}
        >
          <Button variant="outline" className="w-full">
            Cancel Meeting
          </Button>
        </a>
      </div>
    </div>
  )
}

const CompletedCard = ({ t, service_request_type, serviceRequest }) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <img
          className="w-16"
          src="https://www-yns-com.s3.us-east-2.amazonaws.com/articles/images/article-complete.gif"
          alt="complete short course"
        />
        <div className="text-lg font-medium">
          Congratulations! You have completed your {t('title')}. Below will show
          any materials your coach has shared with you.
        </div>
      </div>
      {serviceRequest?.coach_message.length && (
        <div>{parse(serviceRequest.coach_message)}</div>
      )}
      <div className="flex gap-4 pl-2">
        <div className="flex flex-col gap-2 border-r border-gray-600 pr-4">
          <div className="flex items-center text-lg font-medium">
            {' '}
            <YnsIcon name="attachment" className="mr-2 h-6 w-6" />
            Attachments:
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center text-lg font-medium">
            {' '}
            <YnsIcon name="checklist" className="mr-2 h-6 w-6" />
            Checklists:
          </div>
        </div>
      </div>
      {!serviceRequest.feedbacked && (
        <>
          <div className="font-medium">
            How was your experience? Please share your thoughts and feedback!
          </div>
          <div>
            <CandidateReviewFeedbackDialog
              serviceRequestName={t('title')}
              serviceRequestType={service_request_type}
            />
          </div>
        </>
      )}
    </div>
  )
}

const Page = ({ ...props }) => {
  const { service_request_type, coach, current_user, service_request_class } =
    props

  const { serviceRequest, serviceRequestList } = useBenefits()

  const serviceRequestTypeMap = {
    orientation_call: 'OrientationCall',
    job_search_strategy: 'JobSearchStrategy',
    linked_in_review: 'LinkedInReview',
  }

  const ynsIcon = SERVICE_REQUEST_ICONS[service_request_class] || 'phone'

  const calendlyEvent = serviceRequest ? serviceRequest?.calendly_event : null

  const { t } = useTranslation(serviceRequestTypeMap[service_request_type])

  const serviceRequestCompleted = serviceRequest?.completed || false

  const serviceRequestStatus = () => {
    if (serviceRequestCompleted) {
      return 'Completed'
    } else if (calendlyEvent) {
      return 'Scheduled'
    } else if (serviceRequest?.created_at) {
      return 'In Progress'
    } else {
      return 'Not Started'
    }
  }

  const infoCards = [
    {
      title: t('cards.1.title'),
      content:
        parse(t('cards.1.content')) ||
        'This is a placeholder for the first card content. Please replace it with actual content.',
    },
    {
      title: t('cards.2.title'),
      content:
        parse(t('cards.2.content')) ||
        'This is a placeholder for the second card content. Please replace it with actual content.',
    },
    {
      title: t('cards.3.title'),
      content:
        parse(t('cards.3.content')) ||
        'This is a placeholder for the third card content. Please replace it with actual content.',
    },
    {
      title: t('cards.4.title'),
      content:
        parse(t('cards.4.content')) ||
        'This is a placeholder for the fourth card content. Please replace it with actual content.',
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div className="mt-1 flex items-center font-roboto-slab text-xl text-green-800">
        <YnsIcon name={ynsIcon} className="mr-2 h-6 w-6" />
        {t('title')} - {serviceRequestStatus()}
      </div>
      <div className="space-y-4 rounded-2xl border bg-white p-6 shadow-md">
        {serviceRequestCompleted && (
          <CompletedCard
            t={t}
            service_request_type={service_request_type}
            serviceRequest={serviceRequest}
          />
        )}
        {!serviceRequestCompleted &&
          calendlyEvent &&
          !calendlyEvent.cancelled_at && (
            <ScheduledCard calendlyEvent={calendlyEvent} />
          )}
        {(!serviceRequest || !calendlyEvent) && (
          <div>
            <div className="mb-4 text-lg font-medium">
              Ready to meet your Coach? Get started here.
            </div>
            <ScheduleInner
              current_user={current_user}
              services={serviceRequestList}
              coach={coach}
            />
          </div>
        )}
      </div>

      {/* Info Sections */}
      <div className="grid gap-6 md:grid-cols-2">
        {infoCards.map((card, index) => (
          <Card key={index} color="secondary" className="p-4">
            <YnsIcon name="tips" className="mb-2 h-6 w-6" />
            <h3 className="mb-2 flex items-center gap-2 !font-roboto text-lg font-semibold">
              {card.title}
            </h3>
            <div className="text-md leading-relaxed text-gray-700">
              {card.content}
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

const BackToServices = () => {
  return (
    <a
      href="/coach_services/professional_services"
      className="flex items-center gap-2"
    >
      <YnsIcon name="return-left" className="h-6 w-6" />
      Back to All Professional Services
    </a>
  )
}

const ServiceDetail = ({ ...props }) => {
  const { current_user } = props
  return (
    <TranslationProvider
      locale={current_user.locale}
      translations={translations}
      scope="javascript.components.coach_services.ProfessionalServices"
    >
      <BenefitsProvider {...props}>
        <CoachServicesPage description={<BackToServices />}>
          <Page {...props} />
        </CoachServicesPage>
      </BenefitsProvider>
    </TranslationProvider>
  )
}

export default ServiceDetail
